import React, { useEffect } from "react";
import { Outlet, useLocation, useParams } from "react-router-dom";
import { WorkspaceLink, useWsNavigate } from "../../hooks/useWsNavigate";
import AppSidebar from "./Sidebar";
import { ChevronRight, ArrowLeft, FlaskConical, Plus } from "lucide-react";
import { useAgent } from "../../contexts/AgentContext";
import AgentPicker from "../AgentPicker";
import { usePageTitle } from "../../contexts/PageTitleContext";
import GenerationTrackerDock from "../scenarios/GenerationTrackerDock";
import OnboardingFooterBar from "../onboarding/OnboardingFooterBar";
import BillingBanner from "../billing/BillingBanner";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "../ui/sidebar";
import { Separator } from "../ui/separator";

// Fallback page titles derived from route when pages don't set one via context
const ROUTE_TITLES = {
  "/dashboard": "Dashboard",
  "/ai-testing-agent": "Test Agents",
  "/ai-testing-agent/create": "Create Agent",
  "/settings": "Settings",
  "/live-simulations": "Live Simulations",
  "/live-simulations/scenario": "Live Simulations",
  "/live-simulations/persona": "Persona Library",
  "/live-simulations/attributes": "Conversation Attributes",
  "/live-simulations/schedules": "Automated Schedules",
  "/batch-run": "Batch Runs",
  "/evaluation/metrics": "Metrics",
  "/evaluation/metrics/standard": "Metrics",
  "/evaluation/metrics/custom": "Metrics",
  "/evaluation/metrics/global": "Metrics",
  "/evaluation/metrics/optimization": "Metrics",
  "/observability/conversations": "Conversations",
  "/observability/conversations/alerts": "Alerts",
  "/prompt-optimizer": "Prompt Optimizer",
  "/prompt-optimization-diff-viewer": "Prompt Optimizer",
  "/voice-persona": "Persona Library", // legacy — redirects to /live-simulations/persona
  "/analytics": "Analytics",
  "/copilot": "Co-Pilot",
};

// Pages that don't require an agent to exist
const AGENT_EXEMPT_PATHS = [
  "/dashboard",
  "/onboarding",
  "/ai-testing-agent",
  "/ai-testing-agent/create",
  "/settings",
  "/analytics",
];

// Pages that manage their own agent picker inside the page (not in header)
const INLINE_AGENT_PICKER_PATHS = [
  "/evaluation/metrics",
  "/observability/conversations",
  "/live-simulations",
  "/batch-run",
  "/prompt-optimizer",
  "/scheduled-runs",
  "/evaluation/generate-metrics",
  "/evaluation/custom-metrics",
  "/voice-persona",
  "/copilot",
  "/observability/notifications",
];

const NoAgentScreen = () => {
  const navigate = useWsNavigate();
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
      <div className="bg-muted rounded-full p-4 mb-5">
        <FlaskConical className="h-10 w-10 text-muted-foreground" strokeWidth={1.5} />
      </div>
      <h2 className="text-xl font-semibold text-foreground mb-2">
        Set up your first agent
      </h2>
      <p className="text-sm text-muted-foreground max-w-sm mb-6">
        You need to create an AI testing agent before you can use this feature.
        Agents define the voice bot you want to test.
      </p>
      <button
        onClick={() => navigate("/ai-testing-agent/create")}
        className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors"
      >
        <Plus className="h-4 w-4" strokeWidth={2} />
        Create Agent
      </button>
    </div>
  );
};

const Layout = () => {
  const navigate = useWsNavigate();
  const { agents, agentsLoading, selectedAgent, setSelectedAgent } = useAgent();
  const { pageTitle, parentPage, showBackButton } = usePageTitle();
  const location = useLocation();
  const { clientUuid } = useParams();

  // Path with the workspace UUID segment stripped, so all the route-based
  // checks below (and ROUTE_TITLES) keep matching plain paths ("/dashboard").
  const appPath =
    clientUuid && location.pathname.startsWith(`/${clientUuid}`)
      ? location.pathname.slice(clientUuid.length + 1) || "/"
      : location.pathname;

  const isAgentExemptPath =
    AGENT_EXEMPT_PATHS.some(
      (p) => appPath === p || appPath.startsWith(p + "/"),
    ) || appPath.startsWith("/edit-agent/");

  // Hide agent picker on detail/inner pages (URLs containing numeric IDs)
  const isDetailPage = /\/\d+/.test(appPath) || !!parentPage;

  // Show "create agent first" screen when agents have loaded but none exist
  const needsAgent =
    !agentsLoading && agents.length === 0 && !isAgentExemptPath;

  // Derive header title: use context pageTitle if set, otherwise fall back to route map
  const displayTitle =
    pageTitle ||
    (() => {
      const path = appPath;
      if (ROUTE_TITLES[path]) return ROUTE_TITLES[path];
      const match = Object.keys(ROUTE_TITLES).find(
        (r) => path.startsWith(r + "/") || path === r,
      );
      return match ? ROUTE_TITLES[match] : null;
    })();

  // Keep the browser tab title in sync with the current page.
  useEffect(() => {
    document.title = displayTitle ? `${displayTitle} - rubricHQ` : "rubricHQ";
  }, [displayTitle]);

  const showAgentPicker =
    !isAgentExemptPath &&
    !isDetailPage &&
    !INLINE_AGENT_PICKER_PATHS.some(
      (p) => appPath === p || appPath.startsWith(p + "/"),
    ) &&
    agents.length > 0;

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        {/* Top header bar (shadcn dashboard-01) */}
        <header className="sticky top-0 z-10 flex h-14 shrink-0 items-center gap-2 border-b border-border bg-background px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-1 h-4" />

          {showBackButton && parentPage ? (
            <>
              <button
                onClick={() => navigate(parentPage.href)}
                className="p-2 hover:bg-muted rounded-lg transition-colors -ml-1"
                title="Go back"
              >
                <ArrowLeft className="h-5 w-5" strokeWidth={1.5} />
              </button>
              <div className="h-4 w-px bg-border" />
            </>
          ) : null}

          {(displayTitle || parentPage) && (
            <div className="flex items-center gap-2">
              {parentPage && (
                <>
                  <WorkspaceLink
                    to={parentPage.href}
                    className="text-base font-medium text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {parentPage.name}
                  </WorkspaceLink>
                  <ChevronRight
                    className="h-4 w-4 text-muted-foreground"
                    strokeWidth={1.5}
                  />
                </>
              )}
              <h1 className="text-base font-medium text-foreground">
                {displayTitle}
              </h1>
              {showAgentPicker && (
                <>
                  <div className="h-5 w-px bg-border mx-1" />
                  <AgentPicker
                    agents={agents}
                    selectedAgent={selectedAgent}
                    setSelectedAgent={setSelectedAgent}
                    onAdd={() => navigate("/ai-testing-agent/create")}
                  />
                </>
              )}
            </div>
          )}
        </header>

        {/* Billing banner */}
        <BillingBanner />

        {/* Page content rendered by nested route */}
        <div
          className={
            appPath === "/analytics"
              ? "flex-1 flex flex-col overflow-hidden"
              : "flex-1 overflow-auto p-4"
          }
        >
          {needsAgent ? <NoAgentScreen /> : <Outlet />}
        </div>
        {/* Docked inside the content column so it never overlays the sidebar
            (and its user/logout dropdown). */}
        <OnboardingFooterBar />
      </SidebarInset>
      <GenerationTrackerDock />
    </SidebarProvider>
  );
};

export default Layout;
